import type { SvgIconComponent } from "@mui/icons-material";
import AccountBalanceRoundedIcon from "@mui/icons-material/AccountBalanceRounded";
import SavingsRoundedIcon from "@mui/icons-material/SavingsRounded";
import TollRoundedIcon from "@mui/icons-material/TollRounded";
import { formatCurrencyAmountOrMasked } from "shared/money/balances";
import type { LiquidityPool } from "features/wallet/api/Wallet";

export const USD_LIKE_CURRENCIES = new Set(["USD", "USDT", "USDC", "DAI"]);

export interface WalletAccountSection {
    key: string;
    pools: LiquidityPool[];
    balancesByCurrency: Record<string, number>;
}

export interface WalletAccountIconModel {
    Icon?: SvgIconComponent;
    letter?: string;
    color: string;
    background: string;
}

export function sectionKeyForCurrency(currency: string): string {
    const normalized = (currency || "ARS").toUpperCase();
    return USD_LIKE_CURRENCIES.has(normalized) ? "USD" : normalized;
}

export function displayCurrencyForPool(currency: string): string {
    const normalized = (currency || "ARS").toUpperCase();
    return USD_LIKE_CURRENCIES.has(normalized) ? "USD" : normalized;
}

export function formatSignedCurrencyAmountOrMasked(
    amount: number,
    currency: string,
    visible: boolean,
    locale: string,
): string {
    const label = formatCurrencyAmountOrMasked(amount, currency, visible, locale);
    if (!visible) return label;
    return amount < -0.01 ? `-${label}` : label;
}

export function buildCurrencySections(pools: LiquidityPool[]): WalletAccountSection[] {
    const bySection = new Map<string, WalletAccountSection>();

    for (const pool of pools) {
        const sectionKey = sectionKeyForCurrency(pool.currency);
        if (!bySection.has(sectionKey)) {
            bySection.set(sectionKey, {
                key: sectionKey,
                pools: [],
                balancesByCurrency: {},
            });
        }

        const section = bySection.get(sectionKey)!;
        section.pools.push(pool);
        const currency = pool.currency || sectionKey;
        section.balancesByCurrency[currency] = (section.balancesByCurrency[currency] ?? 0) + pool.currentBalance;
    }

    return Array.from(bySection.values())
        .map((section) => ({
            ...section,
            pools: [...section.pools].sort((a, b) => Math.abs(b.currentBalance) - Math.abs(a.currentBalance)),
        }))
        .sort((a, b) => {
            const order = ["ARS", "USD"];
            const aIndex = order.indexOf(a.key);
            const bIndex = order.indexOf(b.key);
            if (aIndex !== -1 || bIndex !== -1) {
                return (aIndex === -1 ? 99 : aIndex) - (bIndex === -1 ? 99 : bIndex);
            }
            return a.key.localeCompare(b.key);
        });
}

export function iconForPool(pool: LiquidityPool, index: number): WalletAccountIconModel {
    const name = pool.name.toLowerCase();
    const currency = (pool.currency || "").toUpperCase();

    if (name.includes("plazo") || name.includes("ahorro")) {
        return { Icon: SavingsRoundedIcon, color: "#D8B4FF", background: "rgba(157, 78, 221, 0.24)" };
    }
    if (currency === "USDT" || currency === "USDC" || name.includes("nexo") || name.includes("crypto")) {
        return { Icon: TollRoundedIcon, color: "#2DDA7D", background: "rgba(45, 218, 125, 0.18)" };
    }
    if (name.includes("galicia") || name.includes("ciudad") || name.includes("banco")) {
        return { Icon: AccountBalanceRoundedIcon, color: "#F4D19A", background: "rgba(244, 162, 97, 0.18)" };
    }

    const variants = [
        { color: "#CFE4FF", background: "rgba(77, 141, 255, 0.20)" },
        { color: "#D8B4FF", background: "rgba(157, 78, 221, 0.20)" },
        { color: "#F8F4FF", background: "rgba(77, 93, 130, 0.24)" },
    ];
    const variant = variants[index % variants.length];
    const letter = (pool.name.trim()[0] || currency[0] || "C").toUpperCase();
    return { letter, ...variant };
}
